import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, BookOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import BrochureModal from "@/components/BrochureModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { trackEvent } from "@/lib/analytics";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const navLinks = [
    { to: "/", label: t("nav.home"), exact: true, testKey: "accueil" },
    { to: "/prestations", label: t("nav.services"), testKey: "prestations" },
    { to: "/photobooth", label: "Photobooth", testKey: "photobooth" },
    { to: "/billetterie", label: t("nav.ticketing"), festive: true, testKey: "billetterie" },
    { to: "/galerie", label: t("nav.gallery"), testKey: "galerie" },
    { to: "/blog", label: t("nav.blog"), testKey: "blog" },
    { to: "/avis", label: t("nav.reviews"), testKey: "avis" },
    { to: "/faq", label: t("nav.faq"), testKey: "faq" },
    { to: "/contact", label: t("nav.contact"), testKey: "contact" },
  ];

  const PRESTATIONS_MENU = [
    { to: "/mariage", label: "Mariage" },
    { to: "/anniversaire", label: "Anniversaire" },
    { to: "/entreprises", label: "Entreprises" },
    { to: "/dj-bar-restaurant-lille", label: "Bars & Restaurants" },
    { to: "/collectivites", label: "Collectivités & Mairies" },
    { to: "/associations", label: "Associations" },
    { to: "/baptemes-communions", label: "Baptêmes & Communions" },
    { to: "/seniors", label: "Résidences Seniors / EHPAD" },
    { to: "/photobooth", label: "Photobooth" },
    { to: "/location", label: "Location de matériel" },
    { to: "/prestations/livre-d-or", label: "Livre d'Or" },
    { to: "/photo-video", label: "Photo & Vidéo" },
    { to: "/musiciens-live", label: "Musiciens Live" },
    { to: "/blind-test-karaoke", label: "Blind Test & Karaoké" },
    { to: "/maxence-berche", label: "Chanteur Live · Maxence Berche" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSubmenuOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/80 border-b border-[#DCB457]/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="font-serif-luxe text-2xl md:text-3xl tracking-tight gold-text">
            My Animation
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7" data-testid="primary-nav">
          {navLinks.map((l) =>
            l.to === "/prestations" ? (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={() => setSubmenuOpen(true)}
                onMouseLeave={() => setSubmenuOpen(false)}
              >
                <NavLink
                  to={l.to}
                  data-testid="nav-prestations"
                  className={({ isActive }) =>
                    `text-[12px] uppercase tracking-[0.18em] transition-colors inline-flex items-center gap-1 ${
                      isActive ? "text-[#DCB457]" : "text-white/70 hover:text-[#DCB457]"
                    }`
                  }
                >
                  {t("nav.services")} <ChevronDown size={12} strokeWidth={1.4} />
                </NavLink>
                {submenuOpen && (
                  <div
                    data-testid="prestations-submenu"
                    className="absolute top-full left-0 pt-4 w-[300px]"
                  >
                    <div className="bg-black/95 backdrop-blur-xl border border-[#DCB457]/20 py-3">
                      {PRESTATIONS_MENU.map((p) => (
                        <Link
                          key={p.to}
                          to={p.to}
                          data-testid={`submenu-${p.to.slice(1)}`}
                          className="block px-5 py-2.5 text-[12px] uppercase tracking-[0.18em] text-white/75 hover:text-[#DCB457] hover:bg-white/[0.03] transition"
                        >
                          {p.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.exact}
                data-testid={`nav-${l.testKey}`}
                className={({ isActive }) => {
                  if (l.festive) {
                    return `text-[12px] uppercase tracking-[0.18em] transition-all font-medium px-3 py-1.5 rounded-sm ${
                      isActive
                        ? "text-white shadow-md"
                        : "text-white hover:opacity-90"
                    }`;
                  }
                  return `text-[12px] uppercase tracking-[0.18em] transition-colors ${
                    isActive ? "text-[#DCB457]" : "text-white/70 hover:text-[#DCB457]"
                  }`;
                }}
                style={l.festive ? {
                  background: "linear-gradient(135deg, #FF3D71 0%, #FF8C42 50%, #7C3AED 100%)",
                  boxShadow: "0 0 14px rgba(255,61,113,0.35)",
                } : undefined}
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setBrochureOpen(true)}
            data-testid="header-brochure-btn"
            className="text-[11px] uppercase tracking-[0.18em] text-white/70 hover:text-[#DCB457] transition inline-flex items-center gap-1.5"
          >
            <BookOpen size={13} strokeWidth={1.4} /> Brochure
          </button>
          <Link
            to="/espace-client"
            data-testid="header-espace-client-btn"
            className="btn-ghost-gold !py-2.5 !px-5 !text-[11px]"
          >
            {t("nav.client_area")}
          </Link>
          <Link
            to="/contact"
            data-testid="header-devis-btn"
            className="btn-gold gold-pulse !py-2.5 !px-5 !text-[11px]"
          >
            {t("common.get_quote")}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
          className="lg:hidden text-white"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} strokeWidth={1.4} /> : <Menu size={26} strokeWidth={1.4} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden absolute left-0 right-0 top-20 bg-black/95 backdrop-blur-xl border-t border-[#DCB457]/20 max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          <div className="px-6 py-7 flex flex-col gap-4">
            <NavLink to="/" className="text-base uppercase tracking-[0.18em] text-white/80" data-testid="mobile-nav-accueil">Accueil</NavLink>
            <div className="border-t border-white/10 pt-4">
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#DCB457] mb-2">Prestations</p>
              {PRESTATIONS_MENU.map((p) => (
                <NavLink
                  key={p.to}
                  to={p.to}
                  data-testid={`mobile-submenu-${p.to.slice(1)}`}
                  className="block py-2 text-sm uppercase tracking-[0.16em] text-white/75"
                >
                  {p.label}
                </NavLink>
              ))}
            </div>
            <div className="border-t border-white/10 pt-4 flex flex-col gap-3">
              <NavLink
                to="/billetterie"
                data-testid="mobile-nav-billetterie"
                className="text-base uppercase tracking-[0.18em] text-white px-4 py-3 rounded font-medium text-center"
                style={{ background: "linear-gradient(135deg, #FF3D71 0%, #FF8C42 50%, #7C3AED 100%)" }}
              >
                ✨ Billetterie / Festivals
              </NavLink>
              <NavLink to="/galerie" data-testid="mobile-nav-galerie" className="text-base uppercase tracking-[0.18em] text-white/80">Galerie</NavLink>
              <NavLink to="/blog" data-testid="mobile-nav-blog" className="text-base uppercase tracking-[0.18em] text-white/80">Blog</NavLink>
              <NavLink to="/avis" data-testid="mobile-nav-avis" className="text-base uppercase tracking-[0.18em] text-white/80">Avis</NavLink>
              <NavLink to="/faq" data-testid="mobile-nav-faq" className="text-base uppercase tracking-[0.18em] text-white/80">FAQ</NavLink>
              <NavLink to="/contact" data-testid="mobile-nav-contact" className="text-base uppercase tracking-[0.18em] text-white/80">Contact</NavLink>
              <NavLink
                to="/espace-client"
                data-testid="mobile-espace-client"
                className="text-base uppercase tracking-[0.18em] text-white/80"
              >
                Espace client
              </NavLink>
            </div>
            <div className="flex flex-col gap-3 pt-4 border-t border-white/10">
              <div className="flex items-center justify-center pb-2">
                <LanguageSwitcher />
              </div>
              <button
                type="button"
                onClick={() => { setBrochureOpen(true); setOpen(false); }}
                data-testid="mobile-brochure-btn"
                className="btn-ghost-gold inline-flex items-center justify-center gap-2"
              >
                <BookOpen size={14} strokeWidth={1.4} /> Télécharger la brochure
              </button>
              <a href="tel:0769224956" onClick={() => { try { trackEvent("click_phone", { location: "header_mobile" }); } catch (_) {} }} data-testid="mobile-call-btn" className="btn-ghost-gold">
                <Phone size={16} strokeWidth={1.4} /> Appeler maintenant
              </a>
              <Link to="/contact" data-testid="mobile-devis-btn" className="btn-gold">
                {t("common.get_quote")}
              </Link>
            </div>
          </div>
        </div>
      )}
      <BrochureModal open={brochureOpen} onClose={() => setBrochureOpen(false)} />
    </header>
  );
}
